import * as React from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
  DiscardGuard,
} from "@/components/ui/dialog";
import { Callout, ErrorState, Skeleton } from "@/components/ui/feedback";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/toast";
import { ApiError } from "@/lib/api";
import { sameValues } from "@/lib/form";
import { useSetUserSkills, useSkills, useUserSkills } from "@/lib/queries";
import {
  DEFAULT_LEVEL, LEVELS, assignableSkills, heldSkills, initialSelection, skillSetChanged,
  skillsBlockedReason, userSkillsPayload, type HeldSkill,
} from "@/lib/skills";
import type { AppUser, RoutingSkill } from "@/lib/types";
import { cn } from "@/lib/utils";

/**
 * A rep's skills: what the ACD matches their phone against (ADR-015).
 *
 * Skills attach to the phone, not to the login: the ACD rings a SIP account, and a rep's levels are
 * keyed by it. An account with no phone therefore has nowhere to hold one, and the dialog says so
 * rather than offering a save core-api refuses.
 *
 * The set is saved whole. `PUT /app-users/{id}/skills` replaces what the rep holds with what it is
 * sent, so the dialog edits a draft of the entire set and `userSkillsPayload` builds it. A skill
 * the rep holds that has since been retired, or is no longer a rep skill, is already ignored by the
 * ACD and cannot be sent back — the PUT would refuse it — so it is listed apart, saying what saving
 * does to it, rather than vanishing from the rep without a word.
 *
 * Supervisors read it. Admins change it, their own included: admins take calls too, and a skill
 * grants routing, not permissions.
 */
export function UserSkillsDialog({
  open, onOpenChange, user, canEdit, isSelf,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Kept while the dialog animates shut, so it does not empty as it closes. */
  user: AppUser | undefined;
  canEdit: boolean;
  isSelf: boolean;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="drawer">
        {/* Radix unmounts the content when the dialog closes, so each opening starts a fresh draft. */}
        {user && (
          <SkillsPanel
            key={user.id}
            user={user}
            canEdit={canEdit}
            isSelf={isSelf}
            onDone={() => onOpenChange(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function SkillsPanel({
  user, canEdit, isSelf, onDone,
}: {
  user: AppUser;
  canEdit: boolean;
  isSelf: boolean;
  onDone: () => void;
}) {
  const toast = useToast();
  const held = useUserSkills(user.id);
  const catalog = useSkills();
  const save = useSetUserSkills();
  /** The set being edited: slug → level. Null until something is touched, so a refetch shows. */
  const [draft, setDraft] = React.useState<Record<string, number> | null>(null);
  const [formError, setFormError] = React.useState<string | null>(null);

  const who = user.display_name || user.email;
  const header = (phone: string | null) => {
    const whose = [isSelf ? null : who, phone].filter(Boolean).join(" · ");
    return (
      <DialogHeader>
        <DialogTitle>{isSelf ? "Your skills" : "Skills"}</DialogTitle>
        <DialogDescription>
          {whose && `${whose}. `}
          The ACD rings a phone only for calls whose required skills it holds, at the level asked.
        </DialogDescription>
      </DialogHeader>
    );
  };

  if (held.isPending || catalog.isPending) {
    return (
      <>
        {header(null)}
        <DialogBody className="space-y-2">
          <Skeleton className="h-12" />
          <Skeleton className="h-12" />
          <Skeleton className="h-12" />
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onDone}>
            Close
          </Button>
        </DialogFooter>
      </>
    );
  }

  if (!held.data || !catalog.data) {
    return (
      <>
        {header(null)}
        <DialogBody>
          <ErrorState
            error={held.error ?? catalog.error}
            title="Could not load the skills"
            action={
              <Button
                variant="outline"
                onClick={() => {
                  void held.refetch();
                  void catalog.refetch();
                }}
              >
                Try again
              </Button>
            }
          />
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onDone}>
            Close
          </Button>
        </DialogFooter>
      </>
    );
  }

  const phone = held.data.user ? `${held.data.user}@${held.data.domain ?? "?"}` : null;
  const blocked = skillsBlockedReason(user, held.data);
  const assignable = assignableSkills(catalog.data, user.tenant_id);
  const rows = heldSkills(held.data.skills, catalog.data, user.tenant_id);
  const dropped = rows.filter((row) => row.problem !== null);
  const initial = initialSelection(held.data.skills, catalog.data, user.tenant_id);
  const selection = draft ?? initial;
  const editable = canEdit && blocked === null;
  const changed = editable && skillSetChanged(selection, held.data.skills, assignable);
  // Not `changed`: a skill the ACD no longer counts makes that true before anything is touched.
  const edited = editable && draft !== null && !sameValues(draft, initial);

  const choose = (slug: string, level: number | null) => {
    const next = { ...selection };
    if (level === null) delete next[slug];
    else next[slug] = level;
    setDraft(next);
  };

  const onSave = async () => {
    setFormError(null);
    try {
      await save.mutateAsync({ userId: user.id, ...userSkillsPayload(selection, assignable) });
      toast.success("Skills saved", who);
      onDone();
    } catch (e) {
      setFormError(e instanceof ApiError ? e.message : "Could not save the skills.");
    }
  };

  return (
    <>
      <DiscardGuard changed={edited} />
      {header(phone)}

      <DialogBody className="space-y-4">
        {formError && <Callout tone="danger">{formError}</Callout>}

        {blocked ? (
          <Callout tone="info">
            {blocked}
            {canEdit && !isSelf && held.data.user === null && " Give them one in Edit user first."}
          </Callout>
        ) : editable ? (
          <>
            {assignable.length === 0 ? (
              <p className="rounded-md border border-dashed border-border px-3 py-4 text-center text-xs text-text-muted">
                The catalog has no rep skills to give.{" "}
                <Link to="/skills" className="text-accent-11 hover:underline">
                  Add one on Skills.
                </Link>
              </p>
            ) : (
              <section className="space-y-2">
                <p className="text-xs text-text-muted">
                  A level runs from 1 to 5. A line asks for a minimum, and a line that rings its best
                  rep first rings the highest.
                </p>
                <ul className="divide-y divide-border rounded-md border border-border">
                  {assignable.map((skill) => (
                    <SkillChoice
                      key={skill.id}
                      skill={skill}
                      level={selection[skill.slug]}
                      onChange={(level) => choose(skill.slug, level)}
                    />
                  ))}
                </ul>
              </section>
            )}

            {dropped.length > 0 && (
              <Callout tone="warning">
                <p>
                  {isSelf ? "You hold" : "They hold"}{" "}
                  {dropped.length === 1
                    ? "a skill the ACD no longer counts. Saving removes it, and it does not come back if the skill is restored."
                    : `${dropped.length} skills the ACD no longer counts. Saving removes them, and they do not come back if the skills are restored.`}
                </p>
                <ul className="mt-1 list-disc space-y-0.5 pl-4">
                  {dropped.map((row) => (
                    <li key={row.slug}>
                      {row.skill?.name ?? row.slug} <span className="font-mono">({row.slug})</span>,
                      level {row.level}: {row.problem?.toLowerCase()}
                    </li>
                  ))}
                </ul>
              </Callout>
            )}
          </>
        ) : (
          <>
            {rows.length === 0 ? (
              <p className="rounded-md border border-dashed border-border px-3 py-4 text-center text-xs text-text-muted">
                {isSelf ? "You hold no skills." : "No skills."}
              </p>
            ) : (
              <ul className="divide-y divide-border rounded-md border border-border">
                {rows.map((row) => (
                  <HeldRow key={row.slug} row={row} />
                ))}
              </ul>
            )}
            <p className="text-xs text-text-muted">An admin sets a rep's skills.</p>
          </>
        )}
      </DialogBody>

      <DialogFooter>
        <Button variant="outline" onClick={onDone}>
          {canEdit ? "Cancel" : "Close"}
        </Button>
        {canEdit && (
          <Button
            variant="default"
            loading={save.isPending}
            disabled={!changed}
            onClick={() => void onSave()}
          >
            Save skills
          </Button>
        )}
      </DialogFooter>
    </>
  );
}

/** One skill the rep can be given: ticked with a level, or not held. The invite form uses it too. */
export function SkillChoice({
  skill, level, onChange, idPrefix = "user-skill",
}: {
  skill: RoutingSkill;
  /** Undefined while not held. */
  level: number | undefined;
  onChange: (level: number | null) => void;
  /** What makes the checkbox's id unique when a page holds more than one of these lists, as the
   *  invite form's does with a list of its own for each person. */
  idPrefix?: string;
}) {
  const id = `${idPrefix}-${skill.id}`;
  const holds = level !== undefined;
  return (
    <li className="flex items-center gap-3 px-3 py-2.5">
      <input
        id={id}
        type="checkbox"
        className="size-3.5 shrink-0 accent-accent-9"
        checked={holds}
        onChange={(e) => onChange(e.target.checked ? DEFAULT_LEVEL : null)}
      />
      <label htmlFor={id} className="min-w-0 flex-1 cursor-pointer">
        <span className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
          <span className="text-sm font-medium text-text">{skill.name}</span>
          <span className="font-mono text-xs text-text-muted">{skill.slug}</span>
          {skill.category && (
            <Badge tone="outline" size="sm">
              {skill.category}
            </Badge>
          )}
        </span>
        {skill.description && (
          <span className="block text-xs text-text-muted">{skill.description}</span>
        )}
      </label>
      {holds ? (
        <Select value={String(level)} onValueChange={(v) => onChange(Number(v))}>
          <SelectTrigger className="w-28 shrink-0" aria-label={`${skill.name} level`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent align="end">
            {LEVELS.map((n) => (
              <SelectItem key={n} value={String(n)}>
                Level {n}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : (
        <span className="w-28 shrink-0 text-right text-xs text-text-subtle">Not held</span>
      )}
    </li>
  );
}

/** A skill as a reader sees it held: its level, and why the ACD ignores it when it does. */
function HeldRow({ row }: { row: HeldSkill }) {
  return (
    <li className="flex items-center gap-3 px-3 py-2.5">
      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
          <span className={cn("text-sm font-medium", row.problem ? "text-text-muted" : "text-text")}>
            {row.skill?.name ?? row.slug}
          </span>
          <span className="font-mono text-xs text-text-muted">{row.slug}</span>
          {row.problem && (
            <Badge tone="warning" size="sm">
              {row.problem}
            </Badge>
          )}
        </span>
        {row.problem && <span className="block text-xs text-text-muted">The ACD does not count it.</span>}
      </span>
      <Badge tone={row.problem ? "outline" : "neutral"} className="shrink-0">
        Level {row.level}
      </Badge>
    </li>
  );
}
